import clsx from 'clsx';

import { useTheme } from '../../lib/theme';

interface Props {
  className?: string;
  /**
   * `compact` — single icon button toggling light/dark.
   * `segmented` — three-way segmented control (system / light / dark).
   */
  variant?: 'compact' | 'segmented';
}

const SUN = (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" aria-hidden>
    <circle
      cx="12"
      cy="12"
      r="4"
      stroke="currentColor"
      strokeWidth="1.6"
    />
    {[
      [12, 2],
      [12, 22],
      [2, 12],
      [22, 12],
      [4.93, 4.93],
      [19.07, 19.07],
      [4.93, 19.07],
      [19.07, 4.93],
    ].map(([x, y], i) => (
      <line
        key={i}
        x1={x}
        y1={y}
        x2={(x as number) + (12 - (x as number)) * 0.45}
        y2={(y as number) + (12 - (y as number)) * 0.45}
        stroke="currentColor"
        strokeWidth="1.6"
        strokeLinecap="round"
      />
    ))}
  </svg>
);

const MOON = (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" aria-hidden>
    <path
      d="M21 13.2A8.5 8.5 0 1 1 10.8 3a7 7 0 0 0 10.2 10.2Z"
      stroke="currentColor"
      strokeWidth="1.6"
      strokeLinejoin="round"
    />
  </svg>
);

export function ThemeToggle({ className, variant = 'compact' }: Props) {
  const { theme, preference, setPreference, toggle } = useTheme();
  if (variant === 'compact') {
    return (
      <button
        type="button"
        onClick={toggle}
        aria-label={theme === 'dark' ? 'Switch to light theme' : 'Switch to dark theme'}
        aria-pressed={theme === 'dark'}
        className={clsx(
          'pb-focus-ring inline-flex h-9 w-9 items-center justify-center rounded-pb-sm border border-pb-border-soft bg-pb-paper text-tg-text transition-colors hover:bg-pb-paper2',
          className,
        )}
      >
        {theme === 'dark' ? SUN : MOON}
      </button>
    );
  }
  return (
    <div
      className={clsx(
        'inline-flex rounded-pb-sm border border-pb-border-soft bg-pb-paper p-0.5 text-[12px]',
        className,
      )}
      role="radiogroup"
      aria-label="Theme"
    >
      {(['system', 'light', 'dark'] as const).map((p) => (
        <button
          key={p}
          type="button"
          role="radio"
          aria-checked={preference === p}
          onClick={() => setPreference(p)}
          className={clsx(
            'rounded-[6px] px-2.5 py-1 capitalize transition-colors',
            preference === p
              ? 'bg-pb-accent text-white'
              : 'text-tg-hint hover:text-tg-text',
          )}
        >
          {p}
        </button>
      ))}
    </div>
  );
}
